import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan, faXmark } from '@fortawesome/free-solid-svg-icons';
import toastService from '@frontend/AppSettings/services/ToastService';
import { calgaryDateString } from '@frontend/utils/calgaryTime';
import { DateLock, DateLockPresetResult, NewDateLock } from './TimesheetDayLockService';
import { WEEKDAY_NAMES, describeDateLock, formatMonthDay, newDateLockFor, serverErrorMessage } from './dayLockHelpers';
import './TimesheetDayLocks.css';

/** The presets the server knows (`holiday_presets.PRESETS`). */
export const HOLIDAY_PRESETS = [{ key: 'alberta_stats', name: 'Alberta stat holidays' }];
/** The server accepts `current year - 1 … current year + 2` (decision g). */
const PRESET_YEARS_BACK = 1;
const PRESET_YEARS_AHEAD = 2;
/** The "Lock a day" date input's bounds; a native date input can otherwise hold a 5- or 6-digit year (HU7). */
const ADD_MIN_DATE = '1900-01-01';
const ADD_MAX_DATE = '2100-12-31';

interface LockedDaysPanelProps {
  open: boolean;
  onClose: () => void;
  lockedWeekdays: number[];
  lockedDates: DateLock[];
  onUnlockWeekday: (weekday: number) => Promise<{ cancelledRequests: number }>;
  onDeleteDateLock: (lock: DateLock) => Promise<{ cancelledRequests: number }>;
  onApplyPreset: (preset: string, year: number) => Promise<DateLockPresetResult>;
  /**
   * Creates a date lock — the same call the header 🔒 makes. With it the drawer offers "Lock a day"
   * (plans/todo/timesheet-lock-days-from-drawer.md, F2); without it nothing new renders.
   */
  onAddDateLock?: (lock: NewDateLock) => Promise<unknown>;
  /** Firm "today" as yyyy-MM-dd — Calgary time unless a test pins it. */
  today?: string;
}

type Pending =
  | { kind: 'weekday'; weekday: number }
  | { kind: 'date'; lock: DateLock }
  | { kind: 'preset'; preset: string; name: string; year: number };

function cancelledNote(count: number): string {
  return count > 0 ? ` ${count} pending request${count === 1 ? ' was' : 's were'} cancelled.` : '';
}

/**
 * The admin "Locked days" drawer (plans/todo/timesheet-date-locks.md, F1): every weekday lock,
 * annual date and one-off date with its label, each removable (🗑, then confirm), and a holiday
 * preset button for each year in range that has no preset dates yet (decision d). Removing a lock
 * cancels the pending requests no remaining lock covers; the toast reports how many.
 * "Lock a day" at the top locks any date, once or every year, in one click with no confirm — adding
 * cancels nothing (plans/todo/timesheet-lock-days-from-drawer.md, decision a).
 */
const LockedDaysPanel: React.FC<LockedDaysPanelProps> = ({
  open,
  onClose,
  lockedWeekdays,
  lockedDates,
  onUnlockWeekday,
  onDeleteDateLock,
  onApplyPreset,
  onAddDateLock,
  today,
}) => {
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [addDate, setAddDate] = useState('');
  const [addLabel, setAddLabel] = useState('');
  const [adding, setAdding] = useState<'date' | 'annual' | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setPending(null);
      setAddDate('');
      setAddLabel('');
    }
  }, [open]);

  if (!open) return null;

  const annual = lockedDates.filter((l) => !l.date);
  const oneOffs = lockedDates.filter((l) => l.date);
  const thisYear = Number((today ?? calgaryDateString()).slice(0, 4));
  const presetYears: number[] = [];
  for (let y = thisYear - PRESET_YEARS_BACK; y <= thisYear + PRESET_YEARS_AHEAD; y += 1) {
    if (!oneOffs.some((l) => l.source === 'preset' && l.date!.startsWith(`${y}-`))) presetYears.push(y);
  }
  const empty = lockedWeekdays.length === 0 && lockedDates.length === 0;
  const locking = busy || adding !== null;

  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === 'weekday') {
        const plural = `${WEEKDAY_NAMES[pending.weekday]}s`;
        const { cancelledRequests } = await onUnlockWeekday(pending.weekday);
        toastService.success(`${plural} unlocked.${cancelledNote(cancelledRequests)}`);
      } else if (pending.kind === 'date') {
        const { cancelledRequests } = await onDeleteDateLock(pending.lock);
        toastService.success(`${describeDateLock(pending.lock)} unlocked.${cancelledNote(cancelledRequests)}`);
      } else {
        const { added, skipped } = await onApplyPreset(pending.preset, pending.year);
        toastService.success(
          added === 0
            ? `${pending.name} ${pending.year} were already added.`
            : `Added ${added} holiday${added === 1 ? '' : 's'} for ${pending.year}${skipped > 0 ? ` (${skipped} already locked)` : ''}.`,
        );
      }
      setPending(null);
    } catch (err) {
      toastService.error(serverErrorMessage(err) || 'Could not save the change.');
    } finally {
      setBusy(false);
    }
  };

  const confirmRow = (message: string, action: string) => (
    <div className="tdl-lockdays-confirm" role="group" aria-label="Confirm">
      <p>{message}</p>
      <button type="button" className="tdl-btn tdl-btn-danger" onClick={confirm} disabled={busy}>
        {busy ? 'Saving…' : action}
      </button>
      <button type="button" className="tdl-btn" onClick={() => setPending(null)} disabled={busy}>
        Cancel
      </button>
    </div>
  );

  const hasAddDate = /^\d{4}-\d{2}-\d{2}$/.test(addDate) && addDate >= ADD_MIN_DATE && addDate <= ADD_MAX_DATE;
  const addMonth = Number(addDate.slice(5, 7));
  const addDay = Number(addDate.slice(8, 10));
  const oneOffTaken = hasAddDate && oneOffs.some((l) => l.date === addDate);
  const annualTaken = hasAddDate && annual.some((l) => l.month === addMonth && l.day === addDay);
  const addDateText = hasAddDate ? describeDateLock({ date: addDate, month: null, day: null }) : '';
  const addMonthDay = hasAddDate ? formatMonthDay(addDate) : '';

  const addLock = async (shape: 'date' | 'annual') => {
    if (!onAddDateLock || !hasAddDate) return;
    const submitted = { date: addDate, label: addLabel };
    const { lock, what } = newDateLockFor(submitted.date, shape, submitted.label);
    setPending(null);
    setAdding(shape);
    try {
      await onAddDateLock(lock);
      toastService.success(`${what} locked.`);
      // Clear only what is still the submitted input: a close + reopen mid-request may have new text.
      setAddDate((d) => (d === submitted.date ? '' : d));
      setAddLabel((l) => (l === submitted.label ? '' : l));
    } catch (err) {
      toastService.error(serverErrorMessage(err) || `Could not lock ${what}.`);
    } finally {
      setAdding(null);
    }
  };

  const oneOffButtonText = () => {
    if (adding === 'date') return 'Saving…';
    if (!hasAddDate) return 'Lock only this date';
    return oneOffTaken ? `${addDateText} is already locked` : `Lock only ${addDateText}`;
  };

  const annualButtonText = () => {
    if (adding === 'annual') return 'Saving…';
    if (!hasAddDate) return 'Lock every year';
    if (annualTaken) return `${addMonthDay} is already locked every year`;
    // Decision f: button text only — the payload, default label and toast say "Feb 29 every year".
    return `Lock ${addMonthDay} every year${addDate.slice(5) === '02-29' ? ' (leap years only)' : ''}`;
  };

  const removeMessage = 'Pending requests on these days are cancelled unless another lock still covers them.';

  const lockRow = (key: string, when: string, label: string | null, tag: string | null, onRemove: () => void, isPending: boolean) => (
    <React.Fragment key={key}>
      <div className="tdl-lockdays-row">
        <span className="tdl-lockdays-when">{when}</span>
        <span className="tdl-lockdays-label" title={label ?? undefined}>{label}</span>
        {tag && <span className="tdl-lockdays-tag">{tag}</span>}
        <button type="button" className="tdl-lockdays-remove" aria-label={`Remove lock: ${when}`} onClick={onRemove} disabled={locking}>
          <FontAwesomeIcon icon={faTrashCan} />
        </button>
      </div>
      {isPending && confirmRow(`Unlock ${when}? ${removeMessage}`, 'Unlock')}
    </React.Fragment>
  );

  return (
    <aside className="tdl-panel" role="dialog" aria-label="Locked days">
      <div className="tdl-panel-head">
        <h3>Locked days</h3>
        <button type="button" className="tdl-panel-close" onClick={onClose} aria-label="Close locked days">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      <div className="tdl-panel-body">
        {onAddDateLock && (
          <section className="tdl-lockdays-section tdl-lockdays-add" aria-label="Lock a day">
            <h4>Lock a day</h4>
            <input
              type="date"
              className="tdl-popover-input"
              aria-label="Date to lock"
              min={ADD_MIN_DATE}
              max={ADD_MAX_DATE}
              value={addDate}
              onChange={(e) => setAddDate(e.target.value)}
              disabled={adding !== null}
            />
            <input
              type="text"
              className="tdl-popover-input"
              placeholder="Label (optional), e.g. Office closed"
              aria-label="Label for the new lock"
              value={addLabel}
              maxLength={120}
              onChange={(e) => setAddLabel(e.target.value)}
              disabled={adding !== null}
            />
            <div className="tdl-popover-actions tdl-popover-actions-stacked">
              <button type="button" className="tdl-btn" onClick={() => addLock('date')} disabled={locking || !hasAddDate || oneOffTaken}>
                {oneOffButtonText()}
              </button>
              <button type="button" className="tdl-btn" onClick={() => addLock('annual')} disabled={locking || !hasAddDate || annualTaken}>
                {annualButtonText()}
              </button>
            </div>
          </section>
        )}
        {empty && (
          <p className="tdl-panel-empty">
            {onAddDateLock
              ? 'No locked days yet. Lock one above, or use the 🔒 on a day header.'
              : 'No locked days. Use the 🔒 on a day header to lock one.'}
          </p>
        )}
        {lockedWeekdays.length > 0 && (
          <section className="tdl-lockdays-section" aria-label="Weekdays">
            <h4>Every week</h4>
            {[...lockedWeekdays].sort((a, b) => a - b).map((weekday) =>
              lockRow(
                `w${weekday}`,
                `Every ${WEEKDAY_NAMES[weekday]}`,
                null,
                null,
                () => setPending({ kind: 'weekday', weekday }),
                pending?.kind === 'weekday' && pending.weekday === weekday,
              ),
            )}
          </section>
        )}
        {annual.length > 0 && (
          <section className="tdl-lockdays-section" aria-label="Every year">
            <h4>Every year</h4>
            {annual.map((lock) =>
              lockRow(
                lock.lockId,
                describeDateLock(lock).replace(/ every year$/, ''),
                lock.label,
                null,
                () => setPending({ kind: 'date', lock }),
                pending?.kind === 'date' && pending.lock.lockId === lock.lockId,
              ),
            )}
          </section>
        )}
        {oneOffs.length > 0 && (
          <section className="tdl-lockdays-section" aria-label="Dates">
            <h4>Dates</h4>
            {oneOffs.map((lock) =>
              lockRow(
                lock.lockId,
                describeDateLock(lock),
                lock.label,
                lock.source === 'preset' ? 'preset' : null,
                () => setPending({ kind: 'date', lock }),
                pending?.kind === 'date' && pending.lock.lockId === lock.lockId,
              ),
            )}
          </section>
        )}
        {presetYears.length > 0 && (
          <section className="tdl-lockdays-section" aria-label="Holiday presets">
            <h4>Holidays</h4>
            <div className="tdl-lockdays-presets">
              {HOLIDAY_PRESETS.flatMap(({ key, name }) =>
                presetYears.map((year) => (
                  <React.Fragment key={`${key}-${year}`}>
                    <button
                      type="button"
                      className="tdl-btn"
                      onClick={() => setPending({ kind: 'preset', preset: key, name, year })}
                      disabled={locking}
                    >
                      Add {name} {year}
                    </button>
                    {pending?.kind === 'preset' && pending.preset === key && pending.year === year &&
                      confirmRow(`Lock the ${name} for ${year}? Each one becomes a date you can remove on its own.`, 'Add')}
                  </React.Fragment>
                )),
              )}
            </div>
          </section>
        )}
      </div>
    </aside>
  );
};

export default LockedDaysPanel;
